import type { TextHighlightColor } from 'designSystem/components/TextHighlight'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import styled from 'styled-components'

export type SearchHighlightMatch = {
  id: number
  bboxes: NormalizedBBox[]
  isRightPage?: boolean
}

export type SearchHighlightData = {
  matches: SearchHighlightMatch[]
  activeMatchId: number | null
  color: TextHighlightColor
}

type SearchHighlightOverlayProps = {
  highlights: SearchHighlightData
  imageNatural: { w: number; h: number }
  firstPageWidth?: number
  isMultipage?: boolean
}

const INACTIVE_FILL_OPACITY = 0.55
const ACTIVE_FILL_OPACITY = 1
const PADDING_TO_LINE_HEIGHT = 0.15
const RADIUS_TO_LINE_HEIGHT = 0.08

const HighlightGroup = styled.g`
  pointer-events: none;
`

const HighlightRectShape = styled.rect<{ $color: TextHighlightColor }>`
  fill: ${({ $color }) => `var(--highlight-${$color})`};
`

function getPageLayout(
  match: SearchHighlightMatch,
  imageNatural: { w: number; h: number },
  firstPageWidth: number | undefined,
  isMultipage: boolean | undefined
): { pageWidth: number; xOffset: number } {
  if (!isMultipage || !firstPageWidth) {
    return { pageWidth: imageNatural.w, xOffset: 0 }
  }
  if (match.isRightPage) {
    return {
      pageWidth: imageNatural.w - firstPageWidth,
      xOffset: firstPageWidth,
    }
  }
  return { pageWidth: firstPageWidth, xOffset: 0 }
}

function HighlightRect({
  bbox,
  pageWidth,
  xOffset,
  imageHeight,
  isActive,
  color,
}: {
  bbox: NormalizedBBox
  pageWidth: number
  xOffset: number
  imageHeight: number
  isActive: boolean
  color: TextHighlightColor
}) {
  const lineHeight = bbox.height * imageHeight
  const padding = lineHeight * PADDING_TO_LINE_HEIGHT
  const radius = lineHeight * RADIUS_TO_LINE_HEIGHT
  return (
    <HighlightRectShape
      $color={color}
      x={xOffset + bbox.left * pageWidth - padding}
      y={bbox.top * imageHeight - padding}
      width={bbox.width * pageWidth + padding * 2}
      height={lineHeight + padding * 2}
      rx={radius}
      ry={radius}
      fillOpacity={isActive ? ACTIVE_FILL_OPACITY : INACTIVE_FILL_OPACITY}
      stroke='none'
    />
  )
}

export function SearchHighlightOverlay({
  highlights,
  imageNatural,
  firstPageWidth,
  isMultipage,
}: SearchHighlightOverlayProps) {
  if (highlights.matches.length === 0) return null

  return (
    <HighlightGroup>
      {highlights.matches.flatMap((match) => {
        const isActive = match.id === highlights.activeMatchId
        const { pageWidth, xOffset } = getPageLayout(
          match,
          imageNatural,
          firstPageWidth,
          isMultipage
        )

        return match.bboxes.map((bbox, bboxIdx) => (
          <HighlightRect
            key={`${match.id}-${bboxIdx}`}
            bbox={bbox}
            pageWidth={pageWidth}
            xOffset={xOffset}
            imageHeight={imageNatural.h}
            isActive={isActive}
            color={highlights.color}
          />
        ))
      })}
    </HighlightGroup>
  )
}
